import { useState } from 'react';
import { apiGet } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';

/** GET /analytics/admin/services response shape (ServiceConsumptionAnalyticsService). */
interface ServiceConsumptionEntry {
  serviceType: string;
  bookingCount: number;
  completedCount: number;
  completionRate: number | null;
  revenueUsd: number;
}

const PERIOD_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

const completionTone = (rate: number | null): 'good' | 'warn' | 'bad' | 'neutral' => {
  if (rate === null) return 'neutral';
  if (rate >= 0.8) return 'good';
  if (rate >= 0.5) return 'warn';
  return 'bad';
};

/**
 * ADM-54 (docs/admin-portal.md §17.7) — which services users actually consume. A genuinely new
 * capability, not a wiring gap: `GET /analytics/admin/services` computes booking count, revenue,
 * and completion rate per service type on-read over the `cases` collection — the "Analytics"
 * (Feature Analytics) screen tracks arbitrary frontend-fired events, not core booking activity,
 * so this is a separate report reading real booking data, same 11 canonical service types the
 * Service Cards admin already covers.
 */
export function ServiceAnalyticsPage() {
  const [periodDays, setPeriodDays] = useState(30);
  const { data, loading, error, reload } = useApiResource(
    () => apiGet<ServiceConsumptionEntry[]>(`/analytics/admin/services?periodDays=${periodDays}`),
    [periodDays],
  );

  const sorted = [...(data ?? [])].sort((a, b) => b.bookingCount - a.bookingCount);
  const totalBookings = sorted.reduce((sum, e) => sum + e.bookingCount, 0);
  const totalRevenue = sorted.reduce((sum, e) => sum + e.revenueUsd, 0);

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Service Analytics</h1>
          <p>
            Booking count, revenue, and completion rate per service type — computed from real case
            activity, not manually-fired events. Same 11 service types the Service Cards admin
            prices and enables.
          </p>
        </div>
        <button type="button" className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      <div className="section-toolbar">
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            className={`tab-btn ${periodDays === opt.days ? 'active' : ''}`}
            onClick={() => setPeriodDays(opt.days)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading && !data ? (
        <div className="empty-state">Loading…</div>
      ) : !data || data.length === 0 ? (
        <div className="empty-state">No service consumption data for this period.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
            <div className="form-card" style={{ margin: 0 }}>
              <div className="form-hint">Total bookings</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{totalBookings}</div>
            </div>
            <div className="form-card" style={{ margin: 0 }}>
              <div className="form-hint">Total revenue</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>${totalRevenue.toFixed(2)}</div>
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Bookings</th>
                  <th>Completed</th>
                  <th>Completion rate</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((entry) => (
                  <tr key={entry.serviceType}>
                    <td>{entry.serviceType}</td>
                    <td>{entry.bookingCount}</td>
                    <td>{entry.completedCount}</td>
                    <td>
                      <StatusBadge
                        label={entry.completionRate === null ? '—' : `${(entry.completionRate * 100).toFixed(0)}%`}
                        tone={completionTone(entry.completionRate)}
                      />
                    </td>
                    <td>${entry.revenueUsd.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
